import type { Edge, Node } from "@xyflow/react";
import { isSourceNodeId, sourceLayoutKey } from "./sources";
import type { FlowDefinition, NodeDefinition, TransitionDefinition } from "./types";

interface FlowNodeData {
  kind?: string;
  tileKind?: string;
  tileTitle?: string;
  tileReview?: string;
  tileAssignee?: string;
  mechanics?: boolean;
  maxParallel?: number;
  label: string;
  ownerRole: string;
  requireApproval: boolean;
  /** Set on a materialized forEach child (flow-visualizer-v3 R1) — its batch index, for the "#k" badge. */
  childIndex?: number;
  /**
   * Set on a materialized forEach child: the flow-definition nodeId it ran under. The child's own
   * id (`"read-tree#0"`) has no entry in `flow.nodes[]` — a lookup by `nodeId` for the declared
   * budgets/instructions must use this instead (see RunInspector's `node` prop in FlowRunView.tsx).
   */
  templateNodeId?: string;
  [key: string]: unknown;
}

/** React Flow node type used for declared sources, so they can be told apart everywhere. */
export const SOURCE_NODE_TYPE = "sourceNode";

/**
 * Sources as canvas nodes, plus one edge into each declared consumer.
 *
 * They share the `nodes`/`edges` arrays with real flow nodes so React Flow lays them out, drags
 * them and routes their edges like anything else — and are filtered back out by {@link rfToFlow},
 * which keeps their positions in `layout` under a `src:` key. A source is not a step; it must
 * never end up in `nodes[]`.
 */
export function sourcesToRf(flow: FlowDefinition): {
  nodes: Node[];
  edges: Edge[];
} {
  const layout = flow.layout || {};
  const sources = flow.sources || [];
  const nodes: Node[] = sources.map((s, idx) => {
    const key = sourceLayoutKey(s.sourceId);
    // An unpositioned source is placed to the LEFT OF THE NODE IT FEEDS rather than in a column at
    // the origin: a source's whole job is to show where a step's data comes from, and an edge that
    // wanders across the graph to reach its consumer does not show that. Stacked by index only when
    // several sources feed the same node, or when it feeds nothing yet.
    const firstConsumer = (s.consumers || [])[0];
    const consumerIndex = firstConsumer
      ? (flow.nodes || []).findIndex((n) => n.nodeId === firstConsumer)
      : -1;
    const consumerPos = firstConsumer
      ? (layout[firstConsumer] ??
        (consumerIndex >= 0 ? defaultNodePosition(consumerIndex) : undefined))
      : undefined;
    const fallback = consumerPos
      ? { x: consumerPos.x - 340, y: consumerPos.y + idx * 20 }
      : { x: -300, y: 80 + idx * 120 };
    const pos = layout[key] || fallback;
    return {
      id: key,
      type: SOURCE_NODE_TYPE,
      position: { x: pos.x, y: pos.y },
      data: {
        label: s.name || s.sourceId,
        sourceId: s.sourceId,
        kind: String(s.kind),
      },
    };
  });
  const edges: Edge[] = [];
  for (const s of sources) {
    for (const consumer of s.consumers || []) {
      edges.push({
        id: `${sourceLayoutKey(s.sourceId)}->${consumer}`,
        source: sourceLayoutKey(s.sourceId),
        target: consumer,
        label: "feeds",
        type: "floating",
        className: "fn-source-edge",
      });
    }
  }
  return { nodes, edges };
}

/**
 * Where a flow node sits when the definition carries no `layout` for it. Exported so sources can be
 * placed beside the node they feed even before anyone has arranged and saved the graph — otherwise a
 * brand-new source's edge sets off across the canvas to find its consumer.
 */
export function defaultNodePosition(index: number): { x: number; y: number } {
  return { x: 80, y: 80 + index * 110 };
}

export function flowToRf(flow: FlowDefinition): { nodes: Node[]; edges: Edge[] } {
  const layout = flow.layout || {};
  const flowNodes = flow.nodes || [];
  const nodes: Node[] = flowNodes.map((n, idx) => {
    const pos = layout[n.nodeId] || defaultNodePosition(idx);
    return {
      id: n.nodeId,
      type: "flowNode",
      position: { x: pos.x, y: pos.y },
      data: {
        label: n.name || n.nodeId,
        ownerRole: n.ownerRole || "",
        requireApproval: !!n.requireApproval,
        // Weave (Docit Work 15 §6.1): the EDITOR draws what the RUNNER draws — the node's kind and the
        // work item it is. An author who cannot see that a node is a bug tile until they run it is
        // designing blind, and the two canvases disagreeing is its own bug.
        kind: n.kind,
        tileKind: n.tile && typeof n.tile.kind === "string" ? n.tile.kind : undefined,
        tileTitle: n.tile && typeof n.tile.title === "string" ? n.tile.title : undefined,
        tileReview: n.tile && typeof n.tile.review === "string" ? n.tile.review : undefined,
        tileAssignee: n.tile && typeof n.tile.assignee === "string" ? n.tile.assignee : undefined,
        mechanics: n.tile === false,
        maxParallel:
          n.kind === "fork" && typeof n.fork?.maxParallel === "number"
            ? n.fork.maxParallel
            : undefined,
      } satisfies FlowNodeData,
    };
  });
  const edges: Edge[] = [];
  for (const n of flowNodes) {
    for (const t of n.transitions || []) {
      if (!t.nextNodeId) {
        continue;
      }
      edges.push({
        id: `${n.nodeId}->${t.nextNodeId}:${t.condition}`,
        source: n.nodeId,
        target: t.nextNodeId,
        label: t.condition || "always",
        animated: t.condition === "always",
        type: "floating",
      });
    }
  }
  return { nodes, edges };
}

// ── forEach children as canvas nodes (flow-visualizer-v3 R1) ───────────────
//
// `flow.nodes[]` only ever has the forEach's CHILD TEMPLATE (e.g. "read-tree") — flowToRf's
// normal 1:1 pass draws it once, disconnected, the same whether the run executed 0 or 600
// batches of it. The executor gives each batch its own step id, `<template>#<index>`
// (`childStepId` in thinkinghub-vscode/src/flows/fanOut.ts), and posts statuses/metas keyed by
// that exact string — so the run-side maps already distinguish every batch, the canvas just never
// grew a node to show it on. `parseChildStepId` below is the inverse of `childStepId`, duplicated
// here rather than imported: this package is bundled standalone into every host (knowit-main,
// marketpulse-web, the VS Code webview) and must not reach into one host's `src/` tree, the same
// reason actionLog.ts/runTrace.ts/NodeRunMeta are hand-mirrored host-side instead of imported.

const CHILD_STEP_ID_RE = /^(.+)#(\d+)$/;

/**
 * Parse an observed run id like `"read-tree#3"` into its template nodeId + batch index, or
 * `undefined` when it isn't shaped like a forEach child step id.
 */
export function parseChildStepId(id: string): { templateId: string; index: number } | undefined {
  const m = CHILD_STEP_ID_RE.exec(id);
  if (!m) {
    return undefined;
  }
  const index = Number(m[2]);
  return Number.isFinite(index) ? { templateId: m[1], index } : undefined;
}

/** The `forEach.child` template nodeId a `kind: "forEach"` node declares, or `undefined`. */
function forEachChildOf(n: NodeDefinition): string | undefined {
  const kind = (n as { kind?: unknown }).kind;
  if (kind !== "forEach") {
    return undefined;
  }
  const spec = (n as { forEach?: { child?: unknown } }).forEach;
  return typeof spec?.child === "string" && spec.child ? spec.child : undefined;
}

export interface ForEachChildLayer {
  nodes: Node[];
  edges: Edge[];
  /**
   * Sorted child-step ids for a forEach, keyed by BOTH its declaration node's id (e.g. "analyze")
   * and its child template's id (e.g. "read-tree") — so the inspector can redirect from either
   * one to an executed child instead of reporting "Not executed yet" about a node whose batches
   * ran (R1's inspector fix, `redirectToExecutedChild` in FlowRunView.tsx).
   */
  childrenByAnchor: Map<string, string[]>;
}

const EMPTY_CHILD_LAYER: ForEachChildLayer = {
  nodes: [],
  edges: [],
  childrenByAnchor: new Map(),
};

/** Horizontal gap between sibling child nodes in their row under the forEach. */
const CHILD_ROW_SPACING_X = 170;
/** Vertical offset of the child row below the forEach node's own (laid-out) position. */
const CHILD_ROW_OFFSET_Y = 130;

/**
 * Canvas nodes for forEach children the run actually executed or is executing — one per
 * `<template>#<k>` id seen in `statuses`/`metas` — stacked in a row under the forEach declaration
 * node, with a thin edge from it to each child. The template node itself (drawn once by
 * {@link flowToRf}) is untouched; it stays the definition, the children are the run.
 *
 * `laidOutNodes` supplies the forEach node's actual on-canvas position (after saved-layout or
 * autoLayout has run) — children anchor to wherever it really is, not a second, independent guess.
 */
export function forEachChildrenToRf(
  flow: FlowDefinition,
  observedIds: Iterable<string>,
  laidOutNodes: Node[],
): ForEachChildLayer {
  const flowNodes = flow.nodes || [];
  const ownerByTemplate = new Map<string, NodeDefinition>();
  for (const n of flowNodes) {
    const child = forEachChildOf(n);
    if (child && !ownerByTemplate.has(child)) {
      ownerByTemplate.set(child, n);
    }
  }
  if (ownerByTemplate.size === 0) {
    return EMPTY_CHILD_LAYER;
  }

  const byId = new Map(flowNodes.map((n) => [n.nodeId, n]));
  const posOf = new Map(laidOutNodes.map((n) => [n.id, n.position]));
  const indicesByTemplate = new Map<string, Set<number>>();
  for (const id of observedIds) {
    const parsed = parseChildStepId(id);
    if (!parsed || !ownerByTemplate.has(parsed.templateId)) {
      continue;
    }
    const set = indicesByTemplate.get(parsed.templateId) ?? new Set<number>();
    set.add(parsed.index);
    indicesByTemplate.set(parsed.templateId, set);
  }
  if (indicesByTemplate.size === 0) {
    return EMPTY_CHILD_LAYER;
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const childrenByAnchor = new Map<string, string[]>();

  for (const [templateId, indexSet] of indicesByTemplate) {
    const owner = ownerByTemplate.get(templateId)!;
    const template = byId.get(templateId);
    const ownerPos = posOf.get(owner.nodeId) ?? defaultNodePosition(0);
    const sorted = [...indexSet].sort((a, b) => a - b);
    const startX = ownerPos.x - ((sorted.length - 1) * CHILD_ROW_SPACING_X) / 2;
    const childIds: string[] = [];
    sorted.forEach((index, i) => {
      const childId = `${templateId}#${index}`;
      childIds.push(childId);
      nodes.push({
        id: childId,
        type: "flowNode",
        position: { x: startX + i * CHILD_ROW_SPACING_X, y: ownerPos.y + CHILD_ROW_OFFSET_Y },
        data: {
          label: template?.name || templateId,
          ownerRole: template?.ownerRole || "",
          requireApproval: !!template?.requireApproval,
          childIndex: index,
          templateNodeId: templateId,
        } satisfies FlowNodeData,
      });
      edges.push({
        id: `${owner.nodeId}=>${childId}`,
        source: owner.nodeId,
        target: childId,
        type: "floating",
        className: "fn-child-edge",
      });
    });
    childrenByAnchor.set(templateId, childIds);
    childrenByAnchor.set(owner.nodeId, childIds);
  }

  return { nodes, edges, childrenByAnchor };
}

// ── artifact nodes as canvas nodes (flow-visualizer-v3 R2, W2 DATA half only) ──────────────
//
// `NodeRunMeta.artifacts` (FlowRunView.tsx) already carries every artifact a node produced or was
// handed — `{path, bytes, mediaType, rows?}`, populated by the `node_sources` and spill events
// (flow-data-plane W5/W6, see codebase-map §5) — but nothing on the wire says which lister
// PRODUCED a path versus merely read it; the executor does not record direction. Approximated
// here rather than left unrepresented: whichever lister has the EARLIEST `meta.startedAt` is
// taken as the producer (a file has to be written before anything downstream can read it), and
// every later lister is a consumer. This is a HEURISTIC, not a fact the run recorded — a graph
// that looks more certain than it is would be its own kind of lie, the exact failure mode R1 was
// built to remove from the inspector. A path with only one lister gets just the producer edge —
// there is no second node to point a consumer edge at. `statuses` only breaks a tie when two
// listers both lack a `startedAt` (e.g. a `node_sources` resolution that lands on a node's meta
// BEFORE that node's own `node_started` sets `startedAt`, per codebase-map §5): a `"running"` or
// `"completed"` node still sorts ahead of one that is merely `"pending"`, since "pending" means the
// event that would explain its listing hasn't actually happened yet.
//
// This function only builds the data (nodes/edges/unread flags) — it does not touch the canvas's
// `nodeTypes` registry, does not get wired into FlowRunView's node/edge memos, and draws nothing.
// That wiring (an `artifactNode` component, merging this layer into the combined base nodes/edges
// the way `forEachChildrenToRf`'s output already is) is W2b.

export interface ArtifactMetaEntry {
  path: string;
  bytes: number;
  mediaType: string;
  rows?: number;
  /** Node-WRITTEN file (flow-visualizer-v3 W2c) — a produced artifact by definition. */
  written?: boolean;
  /**
   * Recorded direction (flow-shared-context R1): the executor now SAYS whether a lister produced
   * or consumed the path. Entries without it are from pre-0.5.42 runs and fall back to the
   * start-order heuristic below.
   */
  dir?: "produced" | "consumed";
  /** Registry version this lister saw (R3) — compared to the CURRENT version for staleness. */
  version?: number;
  /** True when the consumption arrived via the node's `subscribes` patterns (R3). */
  subscribed?: boolean;
}

/**
 * The subset of `NodeRunMeta` (FlowRunView.tsx) this transform reads. Duplicated as a structural
 * type rather than imported: `FlowRunView.tsx` imports `transforms.ts`, so importing `NodeRunMeta`
 * back from there would cycle — the same reason `ForEachChildLayer`'s `FlowNodeData` isn't shared
 * either.
 */
export interface ArtifactAwareMeta {
  artifacts?: ArtifactMetaEntry[];
  /** Paths this node was handed as a handle and never actually read (Q3). */
  handleUnread?: string[];
  startedAt?: number;
}

export interface ArtifactNodeData {
  path: string;
  bytes: number;
  mediaType: string;
  rows?: number;
  /** nodeIds whose `handleUnread` names this artifact's path — a warning flag, not decoration. */
  unreadBy: string[];
  /**
   * CURRENT registry version (flow-shared-context R3) — from the host's `artifact_versions` map
   * when present, else the highest version any lister recorded. Absent on pre-0.5.42 runs.
   */
  version?: number;
  /** nodeIds that consumed an OLDER version than the current one — the stale-read warning. */
  staleReaders: string[];
  [key: string]: unknown;
}

export interface ArtifactLayer {
  nodes: Node[];
  edges: Edge[];
}

const EMPTY_ARTIFACT_LAYER: ArtifactLayer = { nodes: [], edges: [] };

/** Horizontal gap between sibling artifact nodes stacked under the same producer. */
const ARTIFACT_ROW_SPACING_X = 170;
/** Vertical offset of an artifact's row below its producer's own (laid-out or default) position. */
const ARTIFACT_ROW_OFFSET_Y = 200;

/**
 * Canvas nodes for every distinct artifact path any node's meta carries — one node per `path`,
 * however many nodes listed it — plus a producer→artifact edge and one artifact→consumer edge per
 * later lister (see the heuristic above). `statuses` is accepted alongside `metas` the same way
 * every other run-state transform threads both through, and used only for the tie-break noted
 * above; it does not otherwise change which paths become nodes.
 */
export function artifactsToRf(
  flow: FlowDefinition,
  metas: Record<string, ArtifactAwareMeta> | undefined,
  statuses: Record<string, string> | undefined,
  /**
   * CURRENT registry version per path (flow-shared-context R3), pushed by the host as
   * `artifact_versions`. A consumed entry whose recorded `version` is behind the current one
   * renders its edge stale. Absent → nothing renders stale, exactly the pre-0.5.42 behaviour.
   */
  artifactVersions?: Record<string, number>,
): ArtifactLayer {
  if (!metas) {
    return EMPTY_ARTIFACT_LAYER;
  }

  // Per path: which nodes listed it, and the ENTRY each lister carried — the entry is where the
  // recorded direction/version lives (flow-shared-context R1), so it must survive per lister
  // rather than being collapsed to the first one seen.
  const entriesByPath = new Map<string, Map<string, ArtifactMetaEntry>>();
  const unreadByPath = new Map<string, Set<string>>();

  for (const [nodeId, meta] of Object.entries(metas)) {
    for (const a of meta?.artifacts ?? []) {
      let perLister = entriesByPath.get(a.path);
      if (!perLister) {
        perLister = new Map();
        entriesByPath.set(a.path, perLister);
      }
      const prior = perLister.get(nodeId);
      // A node can list the same path twice (e.g. consumed a handle AND wrote the file back).
      // Produced wins for the canonical direction — the write is the stronger fact.
      if (
        !prior ||
        ((a.dir === "produced" || a.written) && !(prior.dir === "produced" || prior.written))
      ) {
        perLister.set(nodeId, a);
      }
    }
    for (const path of meta?.handleUnread ?? []) {
      const set = unreadByPath.get(path);
      if (set) {
        set.add(nodeId);
      } else {
        unreadByPath.set(path, new Set([nodeId]));
      }
    }
  }

  if (entriesByPath.size === 0) {
    return EMPTY_ARTIFACT_LAYER;
  }

  const layout = flow.layout || {};
  const flowNodes = flow.nodes || [];
  const definitionIndex = new Map(flowNodes.map((n, idx) => [n.nodeId, idx]));

  // Missing startedAt sorts LAST (a lister that hasn't actually run yet cannot be the producer),
  // with a "running"/"completed" status breaking a tie between two listers that both lack one.
  const sortKey = (nodeId: string): number => {
    const started = metas[nodeId]?.startedAt;
    if (started != null) {
      return started;
    }
    const status = statuses?.[nodeId];
    return status === "running" || status === "completed"
      ? Number.MAX_SAFE_INTEGER - 1
      : Number.MAX_SAFE_INTEGER;
  };

  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const columnByProducer = new Map<string, number>();
  let fallbackIndex = flowNodes.length;

  for (const [path, perLister] of entriesByPath) {
    // Recorded direction first (flow-shared-context R1); the start-order heuristic ONLY covers
    // listers that recorded none (pre-0.5.42 runs, or paths whose producer the run never saw).
    const producers: string[] = [];
    const consumers: Array<{ nodeId: string; entry: ArtifactMetaEntry }> = [];
    const undirected: string[] = [];
    for (const [nodeId, entry] of perLister) {
      if (entry.dir === "produced" || entry.written) {
        producers.push(nodeId);
      } else if (entry.dir === "consumed") {
        consumers.push({ nodeId, entry });
      } else {
        undirected.push(nodeId);
      }
    }
    undirected.sort((a, b) => sortKey(a) - sortKey(b));
    if (producers.length === 0 && undirected.length) {
      producers.push(undirected.shift()!);
    }
    for (const nodeId of undirected) {
      consumers.push({ nodeId, entry: perLister.get(nodeId)! });
    }

    // Canonical display entry: a produced lister's measurement (taken at the write) beats a
    // consumer's copy of it.
    const entry =
      (producers.length ? perLister.get(producers[0]) : undefined) ??
      perLister.values().next().value!;
    const unreadBy = [...(unreadByPath.get(path) ?? [])];
    const artifactId = `artifact:${path}`;

    const maxRecorded = Math.max(0, ...[...perLister.values()].map((e) => e.version ?? 0));
    const currentVersion = artifactVersions?.[path] ?? (maxRecorded > 0 ? maxRecorded : undefined);
    const staleReaders = consumers
      .filter(
        (c) =>
          c.entry.version != null && currentVersion != null && c.entry.version < currentVersion,
      )
      .map((c) => c.nodeId);

    const anchor = producers[0] ?? consumers[0]?.nodeId ?? "";
    const anchorIdx = definitionIndex.get(anchor);
    const anchorPos = layout[anchor] ?? defaultNodePosition(anchorIdx ?? fallbackIndex++);
    const column = columnByProducer.get(anchor) ?? 0;
    columnByProducer.set(anchor, column + 1);

    nodes.push({
      id: artifactId,
      type: "artifactNode",
      position: {
        x: anchorPos.x + column * ARTIFACT_ROW_SPACING_X,
        y: anchorPos.y + ARTIFACT_ROW_OFFSET_Y,
      },
      data: {
        path: entry.path,
        bytes: entry.bytes,
        mediaType: entry.mediaType,
        rows: entry.rows,
        unreadBy,
        ...(currentVersion ? { version: currentVersion } : {}),
        staleReaders,
      } satisfies ArtifactNodeData,
    });

    for (const producer of producers) {
      edges.push({
        id: `${producer}=>${artifactId}`,
        source: producer,
        target: artifactId,
        type: "floating",
        className: "fn-artifact-edge fn-artifact-edge-produced",
      });
    }

    for (const { nodeId, entry: ce } of consumers) {
      const stale = ce.version != null && currentVersion != null && ce.version < currentVersion;
      const classes = [
        "fn-artifact-edge",
        "fn-artifact-edge-consumed",
        ...(ce.subscribed ? ["fn-artifact-edge-subscribed"] : []),
        ...(stale ? ["fn-artifact-edge-stale"] : []),
      ];
      edges.push({
        id: `${artifactId}=>${nodeId}`,
        source: artifactId,
        target: nodeId,
        type: "floating",
        className: classes.join(" "),
        ...(ce.subscribed
          ? { label: stale ? `subscribes · saw v${ce.version}` : "subscribes" }
          : {}),
      });
    }
  }

  return { nodes, edges };
}

export function rfToFlow(
  flow: FlowDefinition,
  nodes: Node[],
  edges: Edge[],
  overrides: Map<string, Partial<NodeDefinition>>,
): FlowDefinition {
  // Re-derive nodes[]: start from existing canonical entries (preserves
  // instructions, toolName, requireApproval, and any unknown fields) and
  // overlay React Flow's position + inline edits from the side panel.
  const byId = new Map((flow.nodes || []).map((n) => [n.nodeId, n]));
  const newNodes: NodeDefinition[] = [];
  const newLayout: Record<string, { x: number; y: number }> = {};

  for (const rfn of nodes) {
    // Sources ride the same canvas arrays but are not steps: keep the position the author dragged
    // them to and leave them out of nodes[].
    if (rfn.type === SOURCE_NODE_TYPE || isSourceNodeId(rfn.id)) {
      newLayout[rfn.id] = {
        x: Math.round(rfn.position.x),
        y: Math.round(rfn.position.y),
      };
      continue;
    }
    const base: NodeDefinition = byId.get(rfn.id) ?? {
      nodeId: rfn.id,
      name: rfn.id,
      transitions: [],
    };
    const patch = overrides.get(rfn.id) || {};
    const merged: NodeDefinition = { ...base, ...patch };
    // Strip transitions — we rebuild them from edges below.
    merged.transitions = [];
    newNodes.push(merged);
    newLayout[rfn.id] = {
      x: Math.round(rfn.position.x),
      y: Math.round(rfn.position.y),
    };
  }

  // Rebuild transitions from React Flow edges, grouped by source. Edges out of a source node are
  // not transitions — they are the source's `consumers`, owned by the sources array.
  for (const e of edges) {
    if (isSourceNodeId(e.source)) {
      continue;
    }
    const src = newNodes.find((n) => n.nodeId === e.source);
    if (!src) {
      continue;
    }
    const condition = typeof e.label === "string" && e.label ? e.label : "always";
    // Carry any extra fields the original transition had (label, and anything a future writer
    // adds): rebuilding an edge from the canvas must not be a way to lose them.
    const previous = (byId.get(e.source)?.transitions || []).find((t) => t.nextNodeId === e.target);
    const transition: TransitionDefinition = {
      ...(previous ?? {}),
      condition,
      nextNodeId: e.target,
    };
    src.transitions = [...(src.transitions || []), transition];
  }

  return { ...flow, nodes: newNodes, layout: newLayout };
}
